import { useState, useEffect } from 'react';
import { X, BarChart3, TrendingUp, MousePointer, Eye, ScrollText, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

import type { Json } from '@/integrations/supabase/types';

interface AnalyticsEvent {
  id: string;
  event_name: string;
  event_properties: Json;
  page_path: string | null;
  session_id: string | null;
  created_at: string;
}

interface EventSummary {
  event_name: string;
  count: number;
  lastSeen: string;
}

export function AnalyticsDashboard() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [summary, setSummary] = useState<EventSummary[]>([]);
  const [activeTab, setActiveTab] = useState<'live' | 'summary'>('summary');
  const [isLoading, setIsLoading] = useState(false);

  // Fetch initial events and summary
  useEffect(() => {
    if (!isOpen) return;

    const fetchEvents = async () => {
      setIsLoading(true);
      try {
        // Fetch recent events
        const { data: recentEvents, error } = await supabase
          .from('analytics_events')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) throw error;
        setEvents(recentEvents || []);

        // Calculate summary
        const summaryMap = new Map<string, EventSummary>();
        (recentEvents || []).forEach((event) => {
          const existing = summaryMap.get(event.event_name);
          if (existing) {
            existing.count++;
          } else {
            summaryMap.set(event.event_name, {
              event_name: event.event_name,
              count: 1,
              lastSeen: event.created_at,
            });
          }
        });
        setSummary(Array.from(summaryMap.values()).sort((a, b) => b.count - a.count));
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('analytics-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'analytics_events',
        },
        (payload) => {
          const newEvent = payload.new as AnalyticsEvent;
          console.log('[Analytics Dashboard] New event:', newEvent.event_name);
          
          setEvents((prev) => [newEvent, ...prev].slice(0, 50));
          
          setSummary((prev) => {
            const existing = prev.find((s) => s.event_name === newEvent.event_name);
            if (existing) {
              return prev.map((s) =>
                s.event_name === newEvent.event_name
                  ? { ...s, count: s.count + 1, lastSeen: newEvent.created_at }
                  : s
              ).sort((a, b) => b.count - a.count);
            } else {
              return [
                { event_name: newEvent.event_name, count: 1, lastSeen: newEvent.created_at },
                ...prev,
              ].sort((a, b) => b.count - a.count);
            }
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen]);

  const getEventIcon = (eventName: string) => {
    if (eventName.includes('click')) return <MousePointer className="w-3.5 h-3.5" />;
    if (eventName.includes('scroll')) return <ScrollText className="w-3.5 h-3.5" />;
    if (eventName.includes('view') || eventName.includes('page')) return <Eye className="w-3.5 h-3.5" />;
    return <TrendingUp className="w-3.5 h-3.5" />;
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatEventName = (name: string) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-20 right-4 z-50 bg-purple text-white p-3 rounded-full shadow-lg hover:bg-purple/90 transition-all hover:scale-105"
        title="Open Analytics Dashboard"
      >
        <BarChart3 className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div
      className={`fixed top-20 right-4 z-50 bg-white rounded-xl shadow-2xl border border-purple/20 transition-all duration-300 ${
        isMinimized ? 'w-72 h-12' : 'w-96 max-h-[500px]'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-purple/10 bg-purple/5 rounded-t-xl">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-purple" />
          <span className="font-semibold text-sm text-purple">Analytics Dashboard</span>
          <span className="text-xs text-purple/60 bg-purple/10 px-1.5 py-0.5 rounded">DEV</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-purple/10 rounded transition-colors"
          >
            {isMinimized ? (
              <ChevronUp className="w-4 h-4 text-purple/70" />
            ) : (
              <ChevronDown className="w-4 h-4 text-purple/70" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-purple/10 rounded transition-colors"
          >
            <X className="w-4 h-4 text-purple/70" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Tabs */}
          <div className="flex border-b border-purple/10">
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                activeTab === 'summary'
                  ? 'text-purple border-b-2 border-purple'
                  : 'text-purple/60 hover:text-purple'
              }`}
            >
              Summary
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                activeTab === 'live'
                  ? 'text-purple border-b-2 border-purple'
                  : 'text-purple/60 hover:text-purple'
              }`}
            >
              Live Feed
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto h-[400px]">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin w-6 h-6 border-2 border-purple/30 border-t-purple rounded-full" />
              </div>
            ) : activeTab === 'summary' ? (
              <div className="p-3 space-y-2">
                {summary.length === 0 ? (
                  <p className="text-center text-purple/50 text-sm py-8">
                    No events tracked yet. Navigate around to see analytics.
                  </p>
                ) : (
                  summary.map((item) => (
                    <div
                      key={item.event_name}
                      className="flex items-center justify-between p-3 bg-purple/5 rounded-lg hover:bg-purple/10 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-purple/10 rounded text-purple">
                          {getEventIcon(item.event_name)}
                        </div>
                        <span className="text-sm font-medium text-acai">
                          {formatEventName(item.event_name)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-purple">{item.count}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="divide-y divide-purple/5">
                {events.length === 0 ? (
                  <p className="text-center text-purple/50 text-sm py-8">
                    No events yet. Interact with the page to see live events.
                  </p>
                ) : (
                  events.map((event) => (
                    <div key={event.id} className="p-3 hover:bg-purple/5 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="p-1 bg-purple/10 rounded text-purple">
                            {getEventIcon(event.event_name)}
                          </div>
                          <span className="text-sm font-medium text-acai">
                            {formatEventName(event.event_name)}
                          </span>
                        </div>
                        <span className="text-xs text-purple/50">
                          {formatTime(event.created_at)}
                        </span>
                      </div>
                      {event.page_path && (
                        <p className="text-xs text-purple/60 ml-7">{event.page_path}</p>
                      )}
                      {event.event_properties && Object.keys(event.event_properties).length > 0 && (
                        <div className="ml-7 mt-1 text-xs text-purple/50 font-mono bg-purple/5 p-1.5 rounded">
                          {JSON.stringify(event.event_properties, null, 0).slice(0, 100)}
                          {JSON.stringify(event.event_properties).length > 100 && '...'}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
